<template>
  <div class="skill-dive-lab flat-scroll">
    <div class="skill-dive-lab__navigation">
      <button
        class="skill-dive-lab__go-skill-dive-btn btn btn--small btn--tertiary btn--default-icon-color"
        @click="goToPrevRoute"
      >
        <ArrowLeftIcon />
        <span>{{ comesFromCollection ? 'Go Back' : 'Go to Skill Dive' }}</span>
      </button>
      <div
        class="skill-dive-lab__report-issue btn btn--small btn--tertiary btn--default-icon-color"
        @click.stop="$emit('start-issue')"
      >
        <BugIcon class="skill-dive-lab__report-issue-icon" />
        Encountering issues?
      </div>
    </div>
    <div class="skill-dive-lab__header">
      <div class="skill-dive-lab__header-overview-container">
        <div class="skill-dive-lab__skill-dive-bar">
          <div
            v-if="showTimeAgoBadge"
            class="skill-dive-lab__skill-dive-bar-badge"
          >
            <span class="skill-dive-lab__skill-dive-bar-badge-text">
              {{ addedTimeAgoLabel }}
            </span>

            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="10"
              height="34"
              viewBox="0 0 10 34"
              fill="none"
            >
              <path
                d="M9.60909 1.31442C9.80369 0.672234 9.32309 0.0244141 8.65206 0.0244141H0.0240383V33.0244H0L9.60909 1.31442Z"
                fill="#5937E4"
              />
            </svg>
          </div>
          <img
            class="skill-dive-lab__skill-dive-bar-badge-logo"
            src="@/assets/images/lab-experience/skilldive-logo.png"
          />
          <span
            v-if="isVulnerabilityColl"
            class="skill-dive-lab__vulnerabilities"
          >
            Vulnerabilities
          </span>
        </div>
        <h1 class="skill-dive-lab__title">
          {{ currentLab.name }}
        </h1>
        <h1
          v-if="isVulnerabilityColl"
          class="skill-dive-lab__vulnerabilities-cve"
        >
          {{ currentLab.cve }}
        </h1>
        <p
          v-if="collectionsList && collectionsList.length"
          class="skill-dive-lab__related-collections"
        >
          Find similar labs in
          <span v-for="collection in collectionsList" :key="collection.id">
            <router-link
              :to="{
                name: 'collection-detail',
                params: { id: collection.id },
              }"
            >
              {{ collection.name }}
            </router-link>

            <span
              v-if="
                collectionsList[collectionsList.length - 1].id !== collection.id
              "
            >
              ,&nbsp;
            </span>
          </span>
          {{ collectionsList.length > 1 ? 'collections' : 'collection' }}.
        </p>
        <div class="skill-dive-lab__main-info-tile">
          <span
            v-if="learningAreas.length"
            class="skill-dive-lab__learning-areas-container"
          >
            <LearningAreaLabel
              v-for="learningArea in learningAreas"
              :key="learningArea.id"
              :showIcon="!isMobile"
              :learningArea="learningArea.name"
            />
          </span>
          <span v-if="currentLab.difficulty">
            <DifficultyLabel
              alwaysVisible
              hideIconOnMobile
              :difficulty="currentLab.difficulty"
              uppercase
              :bold="false"
            />
          </span>
          <span> {{ duration }} ESTIMATED TIME </span>
          <span v-if="isVulnerabilityColl">
            <SeverityPill :severity="currentLab.severity" />
          </span>
          <span v-if="isLabFinished" class="skill-dive-lab__completed-label">
            <CheckIcon size="16" /> Completed
          </span>
        </div>
      </div>
      <div class="skill-dive-lab__lab-actions-container">
        <div class="skill-dive-lab__status-tile-bg">
          <div
            class="skill-dive-lab__status-tile-container"
            :class="{
              'skill-dive-lab__status-tile-container--running': labIsRunning,
              'skill-dive-lab__status-tile-container--lab-extend':
                labIsRunning && showExtendButton,
            }"
          >
            <div class="skill-dive-lab__status-tile-content">
              <h5
                class="skill-dive-lab__status-tile-label"
                :class="{
                  'skill-dive-lab__status-tile-label--not-running': labIsNotRunning,
                  'skill-dive-lab__status-tile-label--running': labIsRunning,
                }"
              >
                {{ sesseionStatusLabel }}
                <RotatingContainer v-if="labIsStarting || labIsStopping">
                  <LoaderIcon />
                </RotatingContainer>
              </h5>
              <div
                v-if="labIsRunning"
                class="skill-dive-lab__status-tile-timer"
              >
                TIME REMAINING: {{ displayTime }}
              </div>
              <div
                v-if="waitingIntervalId"
                class="skill-dive-lab__status-tile-timer"
              >
                STARTING IN: {{ waitingTimeFormatted }}
              </div>
              <div
                v-if="otherLabSessionRunning && !labIsStopping"
                class="skill-dive-lab__another-lab-running-tile"
              >
                <p class="skill-dive-lab__another-lab-running-title">
                  <span>Another lab is running</span><br />
                  You can’t start this lab until you stop your other running
                  lab.
                </p>
                <div class="skill-dive-lab__another-lab-running-timer-wrapper">
                  <span class="skill-dive-lab__another-lab-running-timer-label">
                    LAB RUNNING
                  </span>
                  <span class="skill-dive-lab__another-lab-running-timer-time">
                    {{ globalRunningTimeFormatted }}
                  </span>
                </div>
                <div class="skill-dive-lab__another-lab-running-name">
                  {{ globalRunningLabName }}
                </div>
                <div
                  class="skill-dive-lab__another-lab-running-actions-container"
                >
                  <router-link
                    class="btn btn--secondary btn--small"
                    :to="{
                      name: 'lab-page',
                      params: { labId: globalRunningSession.lab.id },
                    }"
                  >
                    Go to lab
                  </router-link>
                  <button
                    class="btn btn--primary btn--small"
                    @click="stopGlobalSession"
                  >
                    Stop lab
                  </button>
                </div>
              </div>
            </div>
            <button
              v-if="showExtendButton && labIsRunning"
              :class="['btn btn--secondary extend-btn']"
              :disabled="isExtendButtonDisabled"
              @click="extendLab"
            >
              Extend lab
            </button>
          </div>
        </div>
        <div
          v-if="labIsStarting || labIsRunning"
          class="skill-dive-lab__lab-actions-caption-container"
        >
          <p class="skill-dive-lab__lab-actions-caption-text">
            {{ captionStatusText }}
          </p>
          <div
            v-if="labIsRunning"
            class="skill-dive-lab__lab-actions-toggle-wrapper"
          >
            <span>MARK FINISHED</span>
            <SwitchCheckbox v-model="isLabFinished" :disabled="isCooling" />
          </div>
        </div>
        <div
          v-if="labIsNotRunning && !otherLabSessionRunning"
          class="skill-dive-lab__lab-actions-settings-container"
        >
          <div class="skill-dive-lab__lab-actions-settings-region">
            <span>Region:</span>

            <DropdownV2
              title="Region"
              :value="regionSelected"
              :options="REGIONS"
              @input="changeRegion"
            />
          </div>
          <div class="skill-dive-lab__lab-actions-settings-keyboard-layout">
            <span>Keyboard layout:</span>

            <DropdownV2
              title="Keyboard layout"
              :value="keyboardLayoutSelected"
              labelKey="country"
              iconKey="flag"
              :options="KEYBOARD_LAYOUTS"
              scrollPadding
              @input="changeKeyboardLayout"
            />
          </div>
        </div>

        <div
          v-if="!otherLabSessionRunning"
          class="skill-dive-lab__lab-actions-buttons-container"
        >
          <button
            v-if="labIsNotRunning"
            class="btn btn--primary"
            :disabled="isSessionLoading"
            @click="startLab"
          >
            Start lab
          </button>
          <template v-if="labIsRunning">
            <button class="btn btn--secondary" @click="stopCurrentLab">
              Stop lab
            </button>
            <a
              class="btn btn--primary"
              :class="{
                'btn btn--secondary': showExtendButton,
              }"
              :href="currentLabSession.pta_url"
              target="_blank"
            >
              Open lab
            </a>
          </template>
        </div>
      </div>
    </div>
    <div class="skill-dive-lab__small-device-banner">
      <InfoIcon class="skill-dive-lab__small-device-banner-icon" />
      <span class="skill-dive-lab__small-device-banner-text">
        Labs can only be accessed on a desktop or laptop. Please switch to a
        larger device to continue.
      </span>
    </div>
    <div class="skill-dive-lab__content">
      <div class="skill-dive-lab__content-tile skill-dive-lab__content-left">
        <h5 class="skill-dive-lab__content-description-header">
          About this lab
        </h5>
        <div
          v-if="currentLab.description_html"
          ref="description"
          class="skill-dive-lab__content-description-text"
          v-html="labContentParsed(currentLab.description_html)"
        />
      </div>

      <div class="skill-dive-lab__content-right">
        <PTACatchTheFlag
          v-if="currentLab.flags && currentLab.flags.length"
          :currentLab="currentLab"
          :sessionStatus="sessionStatus"
          :labsRequestPayload="{
            id: currentLab.id,
          }"
          customClass="skill-dive-lab__content-tile skill-dive-lab__content-flags-tile"
        />

        <div
          class="skill-dive-lab__content-tile skill-dive-lab__content-tabs-tile"
        >
          <div
            v-if="filteredTabs.length"
            class="skill-dive-lab__content-tabs-nav"
          >
            <div
              v-for="tab in filteredTabs"
              :key="tab.key"
              class="skill-dive-lab__content-tabs-nav-item"
              :class="{
                'skill-dive-lab__content-tabs-nav-item--active':
                  activeTab === tab.key,
              }"
              @click="handleTabClick(tab.key)"
            >
              {{ tab.key }}
            </div>
          </div>
          <div
            v-if="activeTab"
            :key="tabKey"
            v-replace-imgs
            class="skill-dive-lab__content-tabs-content markdown-styles"
            v-html="activeTabContent(activeTab)"
          />
        </div>
      </div>
    </div>
    <SingleVideo
      v-if="isPlayingSolutionVideo && currentLab.solution_video"
      :videoId="currentLab.solution_video"
      trigger="lab"
      @update-status="updateStatus"
      @close-video-player="toggleVideoPlayer"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import SingleVideo from '@/components/media/SingleVideo.vue'
import CONSTANTS from '@/constants'
import { contentCommons, labCommons, mixpanelEvent } from '@/helpers/mixins'
import { labContentParser } from '@/helpers/contentParsers'
import DifficultyLabel from '@/components/properties/Difficulty.vue'
import {
  ArrowLeftIcon,
  LoaderIcon,
  InfoIcon,
  CheckIcon,
} from 'vue-feather-icons'

import DropdownV2 from '@/components/forms/elements/DropdownV2.vue'
import RotatingContainer from '@/components/shared/RotatingContainer.vue'
import SwitchCheckbox from '@/components/forms/elements/SwitchCheckbox.vue'
import LearningAreaLabel from '@/components/properties/LearningAreaLabel.vue'
import { format, formatDistanceToNow, differenceInDays } from 'date-fns'
import PTACatchTheFlag from '@/components/modals/lab-modal/PTACatchTheFlag.vue'
import BugIcon from '@/assets/icons/bug_icon.svg'
import SeverityPill from '@/components/properties/SeverityPill.vue'
import DraggableImage from '@/components/shared/DraggableImage.vue'
import Vue from 'vue'

const TABS_CONFIG = [
  { key: 'tasks', contentKey: 'tasks_html' },
  { key: 'reveal solutions', contentKey: 'solutions_html' },
]

const CONTENT_PARSERS = [
  {
    target: '#b#',
    openTag: '<b>',
    closeTag: '</b>',
  },
  {
    target: '__',
    openTag: '<span class="skill-dive-lab--remarked">',
    closeTag: '</span>',
  },
]
const zeroTime = new Date(0, 0, 0, 0, 0, 0).getTime()
const refreshSeconds = 5000
const pingSeconds = 60000

export default {
  name: 'SkillDiveLab',
  components: {
    SingleVideo,
    DifficultyLabel,
    ArrowLeftIcon,
    RotatingContainer,
    InfoIcon,
    LearningAreaLabel,
    SwitchCheckbox,
    DropdownV2,
    PTACatchTheFlag,
    LoaderIcon,
    CheckIcon,
    BugIcon,
    SeverityPill,
  },
  mixins: [contentCommons, labCommons, mixpanelEvent],
  inject: ['getWindowWidth'],
  props: {
    parentContext: {
      type: Object,
      default: () => {},
    },
  },
  data: () => {
    return {
      tabKey: 9999,
      isPlayingSolutionVideo: false,
      hasUpdatedStatus: false,
      isLoading: false,
      globalSessionStopping: false,
      isSessionLoading: false,
      labIsStopping: false,
      activeTab: null,
      REGIONS: CONSTANTS.LABS.REGIONS,
      KEYBOARD_LAYOUTS: CONSTANTS.KEYBOARD_LAYOUTS,
      regionSelected: CONSTANTS.LABS.REGIONS[0],
      keyboardLayoutSelected: CONSTANTS.KEYBOARD_LAYOUTS[2],
      globalRunningTimeIntervalId: null,
      runningTimeIntervalId: null,
      sessionRefreshIntervalId: null,
      sessionPingIntervalId: null,
      waitingIntervalId: null,
      runningTime: 0,
      globalRunningTime: 0,
      waitingTime: 0,
      isChangingState: false,
      timeTillShutdown: 0,
      showExtendButton: false,
      isExtendButtonDisabled: false,
      shutdownAnimationId: null,
      notificationTimeoutId: null,
      notificationShown: false,
      isExtendingCurrently: false,
    }
  },
  computed: {
    ...mapState({
      collectionsList: state => state.labExperience.collectionsList,
      currentLabSession: state => state.labs.currentLabSession,
      runningLabSession: state => state.labs.runningLabSession,
    }),
    ...mapGetters({
      isSkillDiveB2CUser: 'auth/isSkillDiveB2CUser',
    }),
    isMobile() {
      return this.getWindowWidth() < 768
    },
    captionStatusText() {
      if (this.labIsStarting) {
        return "Please keep this window open while you're working on the lab. Labs that don't register activity will be automatically stopped."
      } else if (this.labIsRunning) {
        return 'Open the lab to get started. This will open a new browser window.'
      }

      return ''
    },
    displayTime() {
      return this.timeRemainingTillShutdownFormatted
    },
    comesFromCollection() {
      return (
        this.parentContext.type ===
        CONSTANTS.PARENT_CONTENT_TYPES_KEYS.COLLECTION
      )
    },
    globalRunningTimeFormatted() {
      return format(this.globalRunningTime, 'HH:mm:ss')
    },
    runningTimeFormatted() {
      return format(this.runningTime, 'HH:mm:ss')
    },
    waitingTimeFormatted() {
      return format(this.waitingTime, 'HH:mm:ss')
    },
    sessionStatus() {
      const { SESSION_STATUS } = CONSTANTS.LABS
      const currentSessionStatus = this.currentLabSession?.status

      if (!currentSessionStatus) return SESSION_STATUS.UNSTARTED
      if (this.waitingIntervalId) return SESSION_STATUS.STARTING
      if (this.labIsStopping) return SESSION_STATUS.STOPPING

      return currentSessionStatus
    },
    labIsNotRunning() {
      const { UNSTARTED, TERMINATED } = CONSTANTS.LABS.SESSION_STATUS

      return [UNSTARTED, TERMINATED].includes(this.sessionStatus)
    },
    labIsRunning() {
      return this.sessionStatus === CONSTANTS.LABS.SESSION_STATUS.STARTED
    },
    labIsStarting() {
      return this.sessionStatus === CONSTANTS.LABS.SESSION_STATUS.STARTING
    },
    sesseionStatusLabel() {
      const {
        STARTED,
        STARTING,
        STOPPING,
        TERMINATED,
        ERROR,
      } = CONSTANTS.LABS.SESSION_STATUS
      switch (this.sessionStatus) {
        case STARTED:
          return 'Lab Running'
        case STARTING:
          return 'Lab Starting'
        case STOPPING:
          return 'Lab Stopping'
        case TERMINATED:
        case ERROR:
        default:
          return 'Not running'
      }
    },
    globalRunningSession() {
      return this.currentLab.global_running_session
    },
    otherLabSessionRunning() {
      return (
        this.globalRunningSession &&
        this.globalRunningSession.lab.id !== this.currentLab.id
      )
    },
    globalRunningLabName() {
      return this.globalRunningSession?.lab.name
    },
    duration() {
      const { duration_in_seconds: duration } = this.currentLab
      return duration && this.$duration.durationFromSecs(duration)
    },
    learningAreas() {
      return this.currentLab.learning_areas
    },
    filteredTabs() {
      return TABS_CONFIG.filter(tab => !!this.currentLab[tab.contentKey])
    },
    shouldCreateRefreshInterval() {
      const { SESSION_STATUS } = CONSTANTS.LABS

      const sessionRefreshStatuses = [
        SESSION_STATUS.STARTING,
        SESSION_STATUS.STOPPING,
      ]
      return sessionRefreshStatuses.includes(this.currentLabSession?.status)
    },
    isLabFinished: {
      get() {
        return (
          this.currentLab.user_status === CONSTANTS.CONTENT_USER_STATUS.FINISHED
        )
      },
      set() {
        this.toggleLabStatus()
      },
    },
    mixpanelEventPayload() {
      return {
        lab_id: this.currentLab.id,
        lab_name: this.currentLab.name,
        lab_type: this.currentLab.lab_type,
        ...this.currentLab,
      }
    },
    showTimeAgoBadge() {
      const days = differenceInDays(
        new Date(),
        new Date(this.currentLab.published_date)
      )

      return days <= 7
    },
    addedTimeAgoLabel() {
      const timeAgo = formatDistanceToNow(
        new Date(this.currentLab.published_date),
        {
          addSuffix: true,
        }
      )

      return `Added ${timeAgo}`
    },
    isVulnerabilityColl() {
      return this.collectionsList
        ?.map(coll => coll.id)
        .includes(CONSTANTS.LABS_COLLECTIONS.VULNERABILITIES)
    },
    timeRemainingTillShutdownFormatted() {
      const totalSeconds = Math.max(0, Math.floor(this.timeTillShutdown))
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60
      return [
        hours.toString().padStart(2, '0'),
        minutes.toString().padStart(2, '0'),
        seconds.toString().padStart(2, '0'),
      ].join(':')
    },
  },
  watch: {
    currentLabSession: {
      immediate: true,
      handler(newValue, oldValue) {
        const { STARTING, STARTED } = CONSTANTS.LABS.SESSION_STATUS
        this.handleRefreshInterval()
        this.handlePingInterval()
        this.handleRunningTimeInterval()
        this.handleTimeForShutdown()
        if (oldValue?.status === STARTING && newValue.status === STARTED) {
          this.handleWaitingTime()
        }
      },
    },
    otherLabSessionRunning: {
      immediate: true,
      handler() {
        this.handleGlobalRunningTimeInterval()
      },
    },
  },
  async created() {
    this.activeTab = this.filteredTabs[0]?.key
    if (this.currentLab.metadata?.collections?.length) {
      this.getCollections({
        ids: this.currentLab.metadata.collections.join(','),
      })
    }
  },
  mounted() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  beforeDestroy() {
    this.sessionRefreshIntervalId = clearInterval(this.sessionRefreshIntervalId)
    this.sessionPingIntervalId = clearInterval(this.sessionPingIntervalId)
    this.runningTimeIntervalId = clearInterval(this.runningTimeIntervalId)
    this.waitingIntervalId = clearInterval(this.waitingIntervalId)
    this.markingActive = clearTimeout(this.markingActive)
    this.globalRunningTimeIntervalId = clearInterval(
      this.globalRunningTimeIntervalId
    )
    this.cleanupLabSession()
  },
  methods: {
    ...mapActions('labs', [
      'getRunningLab',
      'getLabById',
      'partialUpdateLabSession',
      'createLabSession',
    ]),
    ...mapActions('labExperience', ['getCollections']),
    increaseRunningTime() {
      this.runningTime += 1000
    },
    increaseGlobalRunningTime() {
      this.globalRunningTime += 1000
    },
    handleRefreshInterval() {
      const { STARTING, STOPPING } = CONSTANTS.LABS.SESSION_STATUS
      const sessionRefreshStatuses = [STARTING, STOPPING]
      const shouldCreateRefreshInterval = sessionRefreshStatuses.includes(
        this.sessionStatus
      )
      if (shouldCreateRefreshInterval && !this.sessionRefreshIntervalId) {
        this.sessionRefreshIntervalId = setInterval(
          this.getSession,
          refreshSeconds
        )
      } else if (!this.shouldCreateRefreshInterval) {
        this.sessionRefreshIntervalId = clearInterval(
          this.sessionRefreshIntervalId
        )
      }
    },
    handlePingInterval() {
      const { STARTED } = CONSTANTS.LABS.SESSION_STATUS

      if (this.sessionStatus === STARTED && !this.sessionPingIntervalId) {
        this.sessionPingIntervalId = setInterval(this.createPing, pingSeconds)
      } else {
        this.sessionPingIntervalId = clearInterval(this.sessionPingIntervalId)
      }
    },
    async createPing() {
      const response = await this.$store.dispatch('labs/createLabPing', {
        id: this.currentLab.id,
      })
      if (response) {
        // if error we should get Lab to update session if there is any, this will stop future pings with shouldCreatePingInterval
        await this.fetchCurrentLab()
      }
    },
    handleRunningTimeInterval() {
      const { STARTED } = CONSTANTS.LABS.SESSION_STATUS

      if (this.sessionStatus === STARTED && !this.runningTimeIntervalId) {
        this.setInitialRunningTime()
        this.runningTimeIntervalId = setInterval(this.increaseRunningTime, 1000)
      } else if (this.sessionStatus !== STARTED) {
        this.runningTime = 0
        this.runningTimeIntervalId = clearInterval(this.runningTimeIntervalId)
      }
    },
    handleGlobalRunningTimeInterval() {
      const { STARTED } = CONSTANTS.LABS.SESSION_STATUS

      if (
        this.globalRunningSession?.status === STARTED &&
        !this.globalRunningTimeIntervalId
      ) {
        this.setInitialGlobalRunningTime()
        this.globalRunningTimeIntervalId = setInterval(
          this.increaseGlobalRunningTime,
          1000
        )
      } else {
        this.globalRunningTime = 0
        this.globalRunningTimeIntervalId = clearInterval(
          this.globalRunningTimeIntervalId
        )
      }
    },
    setInitialGlobalRunningTime() {
      this.globalRunningTime = new Date(
        0,
        0,
        0,
        0,
        0,
        this.globalRunningSession.running_time
      ).getTime()
    },
    setInitialRunningTime() {
      this.runningTime = new Date(
        0,
        0,
        0,
        0,
        0,
        this.currentLabSession.running_time
      ).getTime()
    },
    setInitialWaitingTime() {
      this.waitingTime = new Date(
        0,
        0,
        0,
        0,
        0,
        CONSTANTS.LABS.FIXED_PARAMS.WAITING_TIME
      ).getTime()
    },
    handleTabClick(newTab) {
      this.activeTab = newTab
      this.tabKey = this.generateTabKey()
    },
    async getSession() {
      await this.$store.dispatch('labs/getLabSession', {
        labId: this.currentLab.id,
        sessionId: this.currentLabSession.id,
        id: this.currentLab.id,
      })
    },
    async stopCurrentLab() {
      const { TERMINATED } = CONSTANTS.LABS.SESSION_STATUS

      this.labIsStopping = true
      await this.updateSessionStatus({
        labId: this.currentLab.id,
        sessionId: this.currentLabSession.id,
        parentId: this.parentContext.id,
        parentType: this.parentContext.type,
        status: TERMINATED,
      })
      setTimeout(() => {
        // Need to update Skill Dive remaining time, we give the API 1 sec to sync
        this.$store.dispatch('auth/userMeOnContent')
      }, 1000)
      this.labIsStopping = false
      this.cleanupLabSession()
    },
    async stopGlobalSession() {
      const { TERMINATED } = CONSTANTS.LABS.SESSION_STATUS
      this.labIsStopping = true
      await this.updateSessionStatus({
        labId: this.runningLabSession.lab_id,
        parentId: this.runningLabSession.context.parent_id,
        parentType: this.runningLabSession.context.parent_type,
        sessionId: this.globalRunningSession?.id,
        status: TERMINATED,
        cleanGlobalSession: true,
      })
      this.labIsStopping = false
    },
    async updateSessionStatus(payload) {
      await this.partialUpdateLabSession(payload)

      await this.fetchCurrentLab()
    },
    async startLab() {
      const { UNSTARTED, STARTED } = CONSTANTS.LABS.SESSION_STATUS
      this.resetNotificationState()
      try {
        this.isSessionLoading = true

        await this.createLabSession({
          region: this.regionSelected,
          keyboard_layout: this.keyboardLayoutSelected.code,
          id: this.currentLab.id,
          parentId: this.parentContext.id,
          parentType: this.parentContext.type,
        })

        if (this.currentLab.user_status === UNSTARTED)
          this.setLabStatus(STARTED)

        this.isSessionLoading = false
      } catch (error) {
        this.isSessionLoading = false
      }
    },
    decreaseWaitingTime() {
      this.waitingTime -= 1000
      if (this.waitingTime <= zeroTime) {
        this.waitingIntervalId = clearInterval(this.waitingIntervalId)
      }
    },
    handleWaitingTime() {
      this.setInitialWaitingTime()
      this.waitingIntervalId = setInterval(this.decreaseWaitingTime, 1000)
    },
    async setLabStatus(status) {
      await this.$store.dispatch('content/updateLabStatus', {
        labId: this.currentLab.id,
        status,
        parentId: this.parentContext.id,
        parentType: this.parentContext.type,
      })

      if (status === CONSTANTS.CONTENT_USER_STATUS.STARTED) {
        this.getRunningLab()
        this.sendMixpanelEvent(
          this.eventsConstants.CONTENT_LAB_STARTED,
          this.mixpanelEventPayload
        )
      } else if (status === CONSTANTS.CONTENT_USER_STATUS.FINISHED) {
        this.sendMixpanelEvent(
          this.eventsConstants.CONTENT_LAB_FINISHED,
          this.mixpanelEventPayload
        )
      }
    },
    toggleVideoPlayer() {
      this.isPlayingSolutionVideo = !this.isPlayingSolutionVideo
    },
    async updateStatus() {
      if (!this.hasUpdatedStatus && this.videoId) {
        this.hasUpdatedStatus = true
        await this.$store.dispatch('content/updateVideoStatus', {
          videoId: this.videoId,
          status: CONSTANTS.CONTENT_USER_STATUS.FINISHED,
        })
      }
    },
    changeKeyboardLayout(keyboardLayoutSelected) {
      this.keyboardLayoutSelected = keyboardLayoutSelected
    },
    changeRegion(regionSelected) {
      this.regionSelected = regionSelected
    },
    goToPrevRoute() {
      if (this.comesFromCollection) {
        this.$router.push({
          name: 'collection-detail',
          params: { id: this.parentContext.id },
        })
      } else {
        this.$router.push({ name: 'skill-dive' })
      }
    },
    async fetchCurrentLab() {
      const payload = { id: this.$route.params.labId }
      if (this.parentContext) {
        payload.parentId = this.parentContext.id
        payload.parentType = this.parentContext.type
      }
      await this.getLabById(payload)
    },
    async toggleLabStatus() {
      this.handleContentStatusChangeCooling()

      await this.setLabStatus(
        this.isLabFinished
          ? CONSTANTS.CONTENT_USER_STATUS.STARTED
          : CONSTANTS.CONTENT_USER_STATUS.FINISHED
      )
    },
    activeTabContent(activeTab) {
      return this.filteredTabs.length
        ? this.labContentParsed(
            this.currentLab[
              this.filteredTabs.find(tab => tab.key === activeTab).contentKey
            ]
          )
        : ''
    },
    labContentParsed(content) {
      const htmlContent = document.createElement('html')
      htmlContent.innerHTML = content
      const codeContent = htmlContent.getElementsByTagName('code')
      const images = htmlContent.getElementsByTagName('img')
      if (!codeContent?.length) return content
      for (let i = 0; i < codeContent.length; i++) {
        CONTENT_PARSERS.forEach(parser => {
          const codeElement = codeContent[i]
          codeElement.innerHTML = labContentParser(
            parser,
            codeElement.innerHTML
          )
        })
      }
      return htmlContent.innerHTML
    },
    handleTimeForShutdown() {
      if (this.isSessionTerminated()) {
        this.resetShutdownTimer()
        return
      }

      if (this.hasShutdownTime()) {
        this.calculateTimeTillShutdown()
        if (this.timeTillShutdown > 0 && !this.shutdownAnimationId) {
          this.startShutdownCountdown()
          this.setupNotificationTimeout()
        }
      }
    },
    cleanupLabSession() {
      this.clearShutdownCountdown()
      this.clearNotificationTimeout()
      this.resetShutdownTimer()
      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      )
      this.timeTillShutdown = 0
      this.notificationShown = false
      this.showExtendButton = false
      this.isExtendButtonDisabled = true
    },
    resetNotificationState() {
      this.notificationShown = false
      this.clearNotificationTimeout()
    },
    setupNotificationTimeout() {
      this.clearNotificationTimeout()
      if (
        !this.notificationShown &&
        this.timeTillShutdown > CONSTANTS.LABS.EXTENSION_START_TIME
      ) {
        const timeUntilNotification =
          (this.timeTillShutdown - CONSTANTS.LABS.EXTENSION_START_TIME) * 1000
        this.notificationTimeoutId = setTimeout(() => {
          this.showNotification()
        }, timeUntilNotification)
      }
    },
    clearNotificationTimeout() {
      if (this.notificationTimeoutId) {
        clearTimeout(this.notificationTimeoutId)
        this.notificationTimeoutId = null
      }
    },
    handleVisibilityChange() {
      if (document.hidden) {
        this.clearShutdownCountdown()
        this.setupNotificationTimeout()
      } else {
        this.startShutdownCountdown()
      }
    },
    isSessionTerminated() {
      return this.currentSession?.status === 'terminated'
    },
    resetShutdownTimer() {
      this.timeTillShutdown = 0
      this.notificationShown = false
      this.clearNotificationTimeout()
      this.clearShutdownCountdown()
    },
    hasShutdownTime() {
      const hasTime =
        this.currentLabSession?.shutdown_time &&
        this.currentLabSession?.last_started_at &&
        this.sessionStatus === CONSTANTS.LABS.SESSION_STATUS.STARTED
      return hasTime
    },
    calculateTimeTillShutdown() {
      if (
        this.currentLabSession &&
        this.currentLabSession.shutdown_time &&
        this.currentLabSession.last_started_at
      ) {
        const currentTime = Math.floor(Date.now() / 1000)
        const sessionStartTime = Math.floor(
          new Date(this.currentLabSession.last_started_at).getTime() / 1000
        )
        const sessionDuration = this.currentLabSession.shutdown_time
        const shutdownTime = sessionStartTime + sessionDuration

        this.timeTillShutdown = Math.max(0, shutdownTime - currentTime)
      } else {
        this.timeTillShutdown = 0
      }
    },
    shouldStartCountdown() {
      return this.timeTillShutdown > 0 && !this.shutdownAnimationId
    },
    startShutdownCountdown() {
      this.updateShutdownTimer()
    },
    updateShutdownTimer() {
      this.calculateTimeTillShutdown()
      this.checkNotification()
      this.updateExtendButtonVisibility()
      if (this.timeTillShutdown > 0) {
        this.shutdownAnimationId = requestAnimationFrame(
          this.updateShutdownTimer
        )
      } else {
        this.handleSessionEnd()
      }
    },
    handleSessionEnd() {
      this.clearShutdownCountdown()
      this.clearNotificationTimeout()
      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      )
      if (this.sessionStatus === CONSTANTS.LABS.SESSION_STATUS.STARTED) {
        this.stopCurrentLab()
      }
    },
    checkNotification() {
      if (
        this.isNotificationTime() &&
        !this.notificationShown &&
        !this.currentLabSession.metadata.extend_status
      ) {
        this.showNotification()
        this.clearNotificationTimeout()
      }
    },
    updateExtendButtonVisibility() {
      const isWithinExtensionWindow =
        this.timeTillShutdown <= CONSTANTS.LABS.EXTENSION_START_TIME &&
        this.timeTillShutdown > CONSTANTS.LABS.EXTENSION_END_TIME
      if (isWithinExtensionWindow) {
        const extendStatus = this.currentLabSession?.metadata?.extend_status
        this.showExtendButton =
          !extendStatus ||
          extendStatus === CONSTANTS.LABS.EXTEND_STATUSES.PENDING
        this.isExtendButtonDisabled =
          extendStatus === CONSTANTS.LABS.EXTEND_STATUSES.PENDING ||
          this.isExtendingCurrently
      } else {
        this.showExtendButton = false
        this.isExtendButtonDisabled = true
      }
    },
    clearShutdownCountdown() {
      if (this.shutdownAnimationId) {
        cancelAnimationFrame(this.shutdownAnimationId)
        this.shutdownAnimationId = null
      }
    },
    isNotificationTime() {
      return (
        this.timeTillShutdown <= CONSTANTS.LABS.EXTENSION_START_TIME &&
        this.timeTillShutdown > CONSTANTS.LABS.EXTENSION_START_TIME - 1
      )
    },
    showNotification() {
      if (!('Notification' in window)) return
      if (Notification.permission === 'granted') {
        this.displayNotification()
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            this.displayNotification()
          }
        })
      }
      this.notificationShown = true
    },
    displayNotification() {
      const notification = new Notification('', {
        body: '5 minutes of lab time left. Extend for another 30 minutes?',
        icon: `${window.location.origin}/assets/favicon.ico`,
      })
      notification.onclick = () => {
        window.focus()
        notification.close()
      }
    },
    async extendLab() {
      try {
        this.isExtendingCurrently = true
        this.isExtendButtonDisabled = true
        const params = {
          labId: this.currentLab.id,
          sessionId: this.currentLabSession.id,
          parentId: this.parentContext.id,
          parentType: this.parentContext.type,
        }
        await this.$store.dispatch('labs/extendLabSession', params)
        if (
          this.currentLabSession.metadata.extend_status ===
          CONSTANTS.LABS.EXTEND_STATUSES.SUCCESS
        ) {
          this.showExtendButton = false
          this.isExtendButtonDisabled = true
        }
      } catch (error) {
        this.showExtendButton = false
        this.isExtendButtonDisabled = true
      } finally {
        this.isExtendingCurrently = false
      }
    },
    generateTabKey() {
      return Math.floor(1000 + Math.random() * 9000)
    },
  },
}
Vue.directive('replace-imgs', {
  bind(el) {
    const imgs = el.querySelectorAll('img')
    imgs.forEach(img => {
      const imgSrc = img.getAttribute('src')
      const draggableImageComponent = new (Vue.extend(DraggableImage))({
        propsData: { imgSrc, openRatio: 0.65 },
      }).$mount()

      img.replaceWith(draggableImageComponent.$el)
    })
  },
})
</script>

<style lang="scss" scoped>
.skill-dive-lab {
  display: flex;
  align-items: center;
  flex: 1;
  flex-direction: column;
  min-height: 100vh;
  padding-top: 70px;

  @include mobile {
    padding-top: 100px;
  }

  @include desktop {
    padding: 100px;
  }

  &__loader {
    margin-top: 20vh;
  }
  &__navigation {
    display: flex;
    justify-content: space-between;
    align-self: stretch;
  }

  &__title {
    margin: 17px 0 0;

    color: var(--white);
    font-size: 2.5rem;
    font-style: normal;
    font-weight: 700;
    line-height: 3.25rem; /* 130% */
    font-family: var(--font-bold);

    @include desktop {
      font-size: 3.375rem;
      font-style: normal;
      font-weight: 700;
      line-height: 3.4375rem;
    }
  }
  &__related-collections {
    margin: 10px 0 0;

    color: var(--light-blue-gray);
    font-size: 0.75rem;
    font-style: normal;
    font-weight: 450;
    line-height: 1.5rem;
    span {
      display: inline-block;
    }
    a,
    a:visited {
      display: inline-block;
      color: var(--purple-pale);
    }
    a:hover {
      text-decoration: underline;
    }
  }

  &__skill-dive-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 33px;
    &-badge {
      display: flex;
      align-self: stretch;
      &-text {
        display: flex;
        align-items: center;
        align-self: stretch;
        padding: 5px 10px;
        background: var(--secondary-promo-purple);

        color: var(--white);
        text-align: center;
        font-feature-settings: 'clig' off, 'liga' off;

        font-family: var(--font-book);
        font-size: 0.75rem;
        font-style: normal;
        font-weight: 450;
        line-height: 0.875rem;
      }
      &-logo {
        height: 20px;
      }
    }
  }

  &__main-info-tile {
    display: flex;
    padding: 5px 20px;
    align-items: center;
    margin-top: 20px;
    width: fit-content;

    border-radius: 7px;
    background: var(--glass-dark);

    color: #fff;
    font-feature-settings: 'clig' off, 'liga' off;

    font-family: var(--font-book);
    font-size: 0.75rem;
    font-style: normal;
    font-weight: 450;
    line-height: 0.875rem;

    > span {
      display: flex;
      align-items: center;
      height: 30px;
      white-space: nowrap;
      padding: 0 20px;
      border-right: 1px solid var(--white-50);
      text-transform: uppercase;

      &:first-child {
        padding-left: 0;
      }
      &:last-child {
        border-right: none;
      }
    }
  }
  &__learning-areas-container {
    display: flex;
    gap: 20px;
  }
  &__completed-label {
    display: flex;
    gap: 5px;
    text-transform: capitalize !important;
    color: var(--positive-light);
  }
  &__header {
    display: flex;
    align-self: stretch;
    margin-top: 30px;
    gap: 20px;
    &-overview-container {
      padding: 0 20px;

      @include mobile {
        padding: 0 30px;
      }

      @include desktop {
        flex: 1 0 60%;
        padding: 0;
      }
    }
  }

  &__lab-actions {
    &-container {
      display: none;
      flex: 1 0 40%;
      padding: 50px 30px;

      @include tablet {
        display: initial;
      }
    }

    &-caption {
      &-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 30px;
        margin-top: 15px;
        padding: 0 10px;
      }
      &-text {
        margin: 0;
        color: var(--light-blue-gray);

        font-size: 0.75rem;
        font-style: normal;
        font-weight: 500;
        line-height: 1rem;
      }
    }
    &-toggle-wrapper {
      display: flex;
      gap: 10px;
      color: var(--white);
      white-space: nowrap;
    }
    &-settings {
      &-container {
        display: flex;
        margin-top: 15px;
        padding: 0 10px;
        white-space: nowrap;

        color: var(--light-blue-gray);

        font-size: 0.75rem;
        font-style: normal;
        font-weight: 500;
        line-height: 1rem;
      }

      &-region {
        display: flex;
        align-items: center;
        gap: 5px;
        padding-right: 10px;
        border-right: 1px solid var(--light-blue-gray);
      }
      &-keyboard-layout {
        display: flex;
        align-items: center;
        gap: 5px;
        padding-left: 10px;
      }
    }
    &-buttons-container {
      display: flex;
      gap: 10px;
      justify-content: flex-end;
      margin-top: 30px;
    }
  }
  &__another-lab-running {
    &-tile {
      margin-top: 20px;
      padding: 20px 15px;

      border-radius: 10px;
      border: 1px solid #3e3f49;
      background: var(--glass-dark);
    }
    &-title {
      color: var(--light-blue-gray, #afb0bf);
      font-size: 0.75rem;
      font-weight: 400;
      line-height: 1rem;

      span {
        color: var(--white);
        font-weight: 700;
      }
    }
    &-name {
      margin-top: 5px;

      font-size: 0.875rem;
      font-style: normal;
      font-weight: 500;
      line-height: 1.25rem;
    }

    &-timer {
      &-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 30px;

        font-size: 0.75rem;
        font-style: normal;
        font-weight: 450;
        line-height: 0.875rem;
      }
      &-label {
        color: var(--light-blue-gray);
      }

      &-time {
        display: flex;
        gap: 10px;
        align-items: center;
        &::after {
          content: '';
          width: 13px;
          height: 13px;
          border-radius: 50%;
          filter: drop-shadow(0px 0px 10px var(--positive-light));
          background-color: var(--performance-positive);
        }
      }
    }
    &-actions {
      &-container {
        display: flex;
        justify-content: space-between;
        margin-top: 20px;
        gap: 10px;

        > .btn {
          flex: 1;
        }
      }
    }
  }

  &__status-tile {
    &-bg {
      border-radius: 15px;
      background: var(--dark-brighter);
    }
    &-container {
      display: flex;
      align-items: center;
      justify-content: space-between;

      padding: 20px 30px;
      border-radius: inherit;

      background: var(--glass-mid-gray);
      color: var(--white);

      &--running {
        background: var(--alert-gradient-positive);
      }

      &--lab-extend {
        background: var(--orange-lab-extend-gradient);
      }
    }
    &-another-container {
      display: flex;
      flex-direction: column;
    }
    &-content {
      flex-grow: 1;
    }
    &-label {
      display: flex;
      gap: 10px;
      align-items: center;
      margin: 0;

      font-feature-settings: 'clig' off, 'liga' off;

      font-family: var(--font-bold);
      font-size: 1.25rem;
      font-style: normal;
      font-weight: 700;
      line-height: 1.625rem;

      &::after {
        display: none;
        content: '';
        width: 13px;
        height: 13px;
        border-radius: 50%;
      }
      &--not-running {
        &::after {
          display: initial;
          background-color: var(--tundora-gray);
        }
      }
      &--running {
        &::after {
          display: initial;
          background-color: var(--performance-positive);
        }
      }
    }
    &-timer {
      margin-top: 10px;
    }
  }

  &__small-device-banner {
    display: flex;
    align-self: stretch;
    padding: 15px;
    margin-top: 75px;
    justify-content: center;
    align-items: center;
    gap: 15px;

    background: var(--purple-gradient);
    color: var(--white);

    &-text {
      font-size: 0.875rem;
      font-style: normal;
      font-weight: 500;
      line-height: 1.3125rem; /* 150% */
    }

    @include tablet {
      display: none;
    }
  }
  &__content {
    display: flex;
    flex-direction: column;
    gap: 30px;
    width: 100%;
    margin: 40px 0;
    color: var(--white);

    ::v-deep {
      p {
        word-break: break-word;
      }

      img {
        max-width: 100%;
      }
      a,
      a:visited {
        color: var(--white);
        text-decoration: underline;
      }

      .codehilite {
        background: var(--black);
        white-space: break-spaces;

        .skill-dive-lab--remarked {
          background: var(--secondary-purple);
        }
        code {
          background: none;
        }
      }
    }

    @include mobile {
      margin: 50px 0;
      padding: 0 30px;
    }
    @include desktop {
      flex-direction: row;
      margin: 60px 0;
      padding: 0;
    }

    &-tile {
      display: flex;
      padding: 30px;
      flex-direction: column;
      border-radius: 10px;
      background: var(--glass-mid-gray);

      @include desktop {
        align-self: baseline;
      }
    }

    &-right {
      display: flex;
      flex-direction: column;
      gap: 30px;
      @include desktop {
        flex: 0 0 60%;
        overflow: hidden;
      }
    }

    &-left {
      @include desktop {
        display: flex;
        flex: 0 0 40%;
        overflow: hidden;
      }
    }

    &-description {
      &-header {
        margin: 0;
        font-size: 1.25rem;
        font-style: normal;
        font-weight: 700;
        line-height: 1.625rem;
      }
      &-text {
        margin-top: 30px;
      }
    }

    &-tabs {
      &-tile {
        max-width: 100%;
        gap: 10px;
      }

      &-nav {
        display: flex;

        &-item {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 0 20px 20px;
          transition: all 0.5s;
          min-width: 160px;

          border-bottom: 3px solid transparent;

          color: var(--light-blue-gray);
          text-align: center;
          font-feature-settings: 'clig' off, 'liga' off;
          font-size: 20px;
          font-style: normal;
          font-weight: 450;
          line-height: 26px;
          text-transform: capitalize;
          cursor: pointer;

          &--active {
            color: var(--white);
            border-color: #fff;
          }
        }
      }
    }

    &-flags-tile {
      display: flex;
      flex-direction: column;
      gap: 15px;
      margin: 0 !important;

      padding: 10px 30px 30px 30px !important;
      border: none !important;
      background: var(--glass-mid-gray) !important;
      .pta-flags {
        &__header {
          margin: 20px 0;
          &-title {
            margin: 0;
            color: var(--white);

            font-size: 1.25rem;
            font-style: normal;
            font-weight: 700;
            line-height: 1.625rem;
          }
        }
        &__flag {
          &-wrapper {
            display: flex;
            flex-direction: column;
            gap: 15px;
          }

          .pta-flag {
            margin: 0;
            padding: 20px 15px !important;
            border: 1px solid var(--glass-mid-gray);
          }
        }
      }
    }
  }
  &__vulnerabilities {
    color: var(--positive-light);
    font-size: rem(14px);
    font-style: normal;
    font-weight: 500;
    line-height: rem(20px);
    align-self: flex-end;
    padding-bottom: 3px;
    &-cve {
      color: var(--light-blue-gray);
      font-size: rem(20px);
      font-style: normal;
      font-weight: 450;
      line-height: rem(26px);
      margin: 0;
      @include desktop {
        font-size: rem(33px);
        font-style: normal;
        font-weight: 450;
        line-height: rem(38px);
      }
    }
  }
  .extend-btn {
    border: 1px solid var(--white);
  }
}
</style>
